import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getShareMessages, markShareViewed } from '../services/api';
import { ShareWithMessages } from '../types/sharing';
import { formatTimestamp } from '../utils/dateFormatters';
import PdfReportCard from './PdfReportCard';
import { touchScrollColumn } from './ui/scrollStyles';

interface ShareViewerProps {
  shareId: string;
  onClose: () => void;
  onReply?: (shareId: string) => void;
  onMarkedAsViewed?: () => void; // Callback when share is marked as viewed
}

const ShareViewer: React.FC<ShareViewerProps> = ({ shareId, onClose, onReply, onMarkedAsViewed }) => {
  const [shareData, setShareData] = useState<ShareWithMessages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markedAsViewed, setMarkedAsViewed] = useState(false);

  // Validate shareId prop
  useEffect(() => {
    if (!shareId || shareId.trim().length === 0) {
      setError('Invalid share ID');
      setLoading(false);
      return;
    }
  }, [shareId]);

  const loadShare = useCallback(async () => {
    if (!shareId || shareId.trim().length === 0) return;

    setLoading(true);
    setError(null);

    let isMounted = true;

    try {
      const response = await getShareMessages(shareId);
      if (isMounted) {
        setShareData(response);
      }
    } catch (err: any) {
      console.error('Failed to load shared conversation:', err);
      if (isMounted) {
        setError(err.message || 'Failed to load shared conversation');
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [shareId]);

  useEffect(() => {
    loadShare();
  }, [loadShare]);

  // Mark share as viewed after successful load with 2-second delay
  // This ensures the user has actually seen the content before marking it viewed
  useEffect(() => {
    if (!shareData || error || loading || markedAsViewed) {
      return; // Don't mark if still loading, error occurred, or already marked
    }

    // Check if share is unread
    const isUnread = !shareData.share?.viewedAt;
    if (!isUnread) {
      return; // Already marked as viewed
    }

    console.log('[ShareViewer] Share loaded successfully, will mark as viewed in 2 seconds');

    // Wait 2 seconds to ensure user has time to view the content
    const timer = setTimeout(async () => {
      try {
        console.log('[ShareViewer] Marking share as viewed:', shareId);
        await markShareViewed(shareId);
        setMarkedAsViewed(true);

        // Notify parent component to refresh shares list and unread count
        if (onMarkedAsViewed) {
          onMarkedAsViewed();
        }

        console.log('[ShareViewer] Share marked as viewed successfully');
      } catch (err) {
        console.error('[ShareViewer] Failed to mark share as viewed:', err);
        // Don't show error to user - this is not critical
        // The share will remain unread and user can view it again
      }
    }, 2000); // 2 second delay

    return () => clearTimeout(timer);
  }, [shareData, error, loading, shareId, markedAsViewed, onMarkedAsViewed]);

  // Keyboard support: Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDownloadFile = (file: { name: string; content: string; mimeType: string }) => {
    // Validate file properties
    if (!file || !file.content || !file.name || !file.mimeType) {
      console.error('Invalid file data for download');
      return;
    }

    try {
      const blob = new Blob([file.content], { type: file.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: 'transparent',
    },
    header: {
      padding: '1.5rem 2rem',
      borderBottom: '1px solid rgba(229, 231, 235, 0.3)',
      backgroundColor: 'transparent',
      flexShrink: 0,
    },
    headerContent: {
      maxWidth: '900px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    headerInfo: {
      flex: 1,
      minWidth: 0,
    },
    headerTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      marginBottom: '0.375rem',
    },
    headerMeta: {
      fontSize: '0.8125rem',
      color: 'var(--fg-2)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap',
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center',
    },
    button: {
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'var(--font-sans)',
      border: 'none',
    },
    closeButton: {
      backgroundColor: 'transparent',
      border: '1px solid var(--border-1)',
      color: 'var(--fg-2)',
    },
    replyButton: {
      backgroundColor: 'var(--accent)',
      color: 'white',
    },
    notesBox: {
      maxWidth: '900px',
      margin: '1rem auto',
      padding: '1rem 2rem',
    },
    notesContent: {
      backgroundColor: 'var(--accent-soft)',
      border: '1px solid var(--accent)',
      borderRadius: '8px',
      padding: '0.875rem 1rem',
    },
    notesLabel: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: 'var(--accent)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.375rem',
    },
    notesText: {
      fontSize: '0.9375rem',
      color: 'var(--fg-1)',
      lineHeight: 1.6,
      fontStyle: 'italic',
    },
    messagesContainer: {
      ...touchScrollColumn,
      padding: '2rem 1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem',
    },
    messageRow: {
      maxWidth: '900px',
      width: '100%',
      margin: '0 auto',
      animation: 'fadeInUp 0.5s ease-out',
    },
    userMessageContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    userMessage: {
      maxWidth: '70%',
      padding: '1rem 1.25rem',
      backgroundColor: 'rgba(184, 197, 214, 0.30)',
      color: 'var(--fg-1)',
      borderRadius: '16px',
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    assistantMessageContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    assistantContent: {
      maxWidth: '100%',
      fontSize: '0.9375rem',
      lineHeight: 1.7,
      color: 'var(--fg-1)',
      fontWeight: 300,
    },
    filesContainer: {
      marginTop: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    fileCard: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1rem',
      backgroundColor: 'var(--bg-surface-1)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '12px',
      gap: '1rem',
      transition: 'all 0.2s ease',
    },
    fileInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.375rem',
      flex: 1,
    },
    fileName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: 'var(--fg-1)',
    },
    driveLink: {
      fontSize: '0.8125rem',
      color: 'var(--fg-2)',
      textDecoration: 'none',
      transition: 'color 0.2s ease',
    },
    downloadButton: {
      padding: '0.5rem 1rem',
      backgroundColor: 'var(--cg-midnight-100)',
      color: 'var(--fg-1)',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '0.8125rem',
      fontWeight: 500,
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap',
      fontFamily: 'var(--font-sans)',
    },
    loadingContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: '4px solid var(--border-1)',
      borderTop: '4px solid var(--accent)',
      borderRadius: '50%',
      animation: 'cgConicSpin 1s linear infinite',
    },
    loadingText: {
      fontSize: '0.9375rem',
      color: 'var(--fg-2)',
    },
    errorContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      padding: '2rem',
    },
    errorIcon: {
      fontSize: '3rem',
    },
    errorText: {
      fontSize: '1rem',
      color: 'var(--fg-1)',
      textAlign: 'center',
    },
    errorSubtext: {
      fontSize: '0.875rem',
      color: 'var(--fg-2)',
      textAlign: 'center',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={styles.loadingText}>Loading shared conversation...</p>
        </div>
      </div>
    );
  }

  if (error || !shareData) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>⚠️</div>
          <p style={styles.errorText}>Failed to load shared conversation</p>
          <p style={styles.errorSubtext}>{error || 'Unknown error'}</p>
          <button style={{...styles.button, ...styles.closeButton}} onClick={onClose}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { share, messages } = shareData;

  // Validate messages is an array
  const validMessages = Array.isArray(messages) ? messages : [];
  const fromUserName = share?.fromUserName || 'Unknown User';

  return (
    <div style={styles.container} role="main" aria-label="Shared conversation viewer">
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerInfo}>
            <div style={styles.headerTitle}>
              Shared by {fromUserName}
            </div>
            <div style={styles.headerMeta}>
              <span>{formatTimestamp(share.sharedAt)}</span>
              <span aria-hidden="true">•</span>
              <span>{validMessages.length} message{validMessages.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div style={styles.headerActions}>
            {onReply && (
              <button
                style={{...styles.button, ...styles.replyButton}}
                onClick={() => onReply(shareId)}
                aria-label="Reply to this shared conversation"
              >
                Reply
              </button>
            )}
            <button
              style={{...styles.button, ...styles.closeButton}}
              onClick={onClose}
              aria-label="Close shared conversation"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {share.notes && share.notes.trim().length > 0 && (
        <div style={styles.notesBox}>
          <div style={styles.notesContent}>
            <div style={styles.notesLabel}>Note from {fromUserName}</div>
            <div style={styles.notesText}>"{share.notes}"</div>
          </div>
        </div>
      )}

      <div style={styles.messagesContainer}>
        {validMessages.map((msg) => {
          // Validate message content is a string
          const messageContent = typeof msg.content === 'string' ? msg.content : '';

          return (
            <div key={msg.id} style={styles.messageRow}>
              {msg.role === 'user' ? (
                <div style={styles.userMessageContainer}>
                  <div style={styles.userMessage} role="article" aria-label="User message">
                    {messageContent}
                  </div>
                </div>
              ) : (
                <div style={styles.assistantMessageContainer}>
                  <div style={styles.assistantContent} role="article" aria-label="Assistant message">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ node, children, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        ),
                        img: ({ node, ...props }) => (
                          <img {...props} alt={props.alt || 'Image'} loading="lazy" />
                        ),
                        table: ({ node, children, ...props }) => (
                          <div className="table-wrapper">
                            <table className="markdown-table" {...props}>{children}</table>
                          </div>
                        ),
                        thead: ({ node, children, ...props }) => (
                          <thead className="markdown-thead" {...props}>{children}</thead>
                        ),
                        tbody: ({ node, children, ...props }) => (
                          <tbody className="markdown-tbody" {...props}>{children}</tbody>
                        ),
                        tr: ({ node, children, ...props }) => (
                          <tr className="markdown-tr" {...props}>{children}</tr>
                        ),
                        th: ({ node, children, ...props }) => (
                          <th className="markdown-th" {...props}>{children}</th>
                        ),
                        td: ({ node, children, ...props }) => (
                          <td className="markdown-td" {...props}>{children}</td>
                        )
                      }}
                    >
                      {messageContent}
                    </ReactMarkdown>
                  {msg.files && msg.files.length > 0 && (
                    <div style={styles.filesContainer}>
                      {msg.files.map((file, index) => (
                        <div key={index} style={styles.fileCard}>
                          <div style={styles.fileInfo}>
                            <span style={styles.fileName}>{file.name}</span>
                            {file.driveLink && (
                              <a
                                href={file.driveLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={styles.driveLink}
                              >
                                Open in Drive →
                              </a>
                            )}
                          </div>
                          <button
                            onClick={() => handleDownloadFile(file)}
                            style={styles.downloadButton}
                            aria-label={`Download ${file.name || 'file'}`}
                          >
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.pdfReports && msg.pdfReports.length > 0 && (
                    <div>
                      {msg.pdfReports.map((report: any, reportIndex: number) => (
                        <PdfReportCard key={report.id || reportIndex} report={report} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default ShareViewer;
